import { useMutation } from '@tanstack/react-query';

import { EntryTypeCreateForm, EntryTypeData, EntryTypeStatus } from 'chaika-frontend-types/frontend-cms';

import { EntryTypeApi } from '@api/EntryTypeApi';
import { queryClient } from '@store/queryClient';

import { EntryTypeDraft } from '../types';
import { makeEntryTypeSaveParams } from '../utils/entryTypeSaveParams';
import { updateEntryType } from '../utils/entryTypeDraftPatch';
import { editEntryTypeKeys } from './editEntryTypeQueries';

interface EntryTypeFieldsUpdate extends Partial<
    Pick<EntryTypeData, 'title' | 'titleTranslations' | 'type' | 'isNested' | 'isDefault' | 'legalEntityIds'>
> {}

interface UpdateEntryTypeFieldsVariables {
    entryTypeId: string;
    version: number;
    params: EntryTypeFieldsUpdate;
}

interface EntryTypeServiceVariables {
    entryTypeId: string;
    version: number;
    serviceId: string;
}

interface PublishEntryTypeVariables {
    entryTypeId: string;
    version: number;
}

interface CreateEntryTypeVersionVariables {
    entryTypeId: string;
    params: EntryTypeCreateForm;
}

export function useSaveEntryTypeDraft() {
    return useMutation({
        mutationFn: (draft: EntryTypeDraft) =>
            EntryTypeApi.update(draft.id, draft.version, makeEntryTypeSaveParams(draft)),
    });
}

export function useUpdateEntryTypeFields() {
    return useMutation({
        mutationFn: ({ entryTypeId, version, params }: UpdateEntryTypeFieldsVariables) =>
            EntryTypeApi.update(entryTypeId, version, params),
        onSuccess: (_data, { entryTypeId, version, params }) => {
            setDraft(entryTypeId, version, draft => updateEntryType(draft, params));
        },
    });
}

export function useAddEntryTypeService() {
    return useMutation({
        mutationFn: ({ entryTypeId, version, serviceId }: EntryTypeServiceVariables) =>
            EntryTypeApi.addService(entryTypeId, version, serviceId),
        onSuccess: (_data, { entryTypeId, version, serviceId }) => {
            setServiceIds(entryTypeId, version, serviceIds => [...serviceIds, serviceId]);
        },
    });
}

export function useRemoveEntryTypeService() {
    return useMutation({
        mutationFn: ({ entryTypeId, version, serviceId }: EntryTypeServiceVariables) =>
            EntryTypeApi.removeService(entryTypeId, version, serviceId),
        onSuccess: (_data, { entryTypeId, version, serviceId }) => {
            setServiceIds(entryTypeId, version, serviceIds => serviceIds.filter(id => id !== serviceId));
        },
    });
}

export function usePublishEntryType() {
    return useMutation({
        mutationFn: ({ entryTypeId, version }: PublishEntryTypeVariables) =>
            EntryTypeApi.updateStatus(entryTypeId, version, EntryTypeStatus.Active),
        onSuccess: (_data, { entryTypeId, version }) => {
            setDraft(entryTypeId, version, draft => updateEntryType(draft, { status: EntryTypeStatus.Active }));
        },
    });
}

export function useCreateEntryTypeVersion() {
    return useMutation({
        mutationFn: ({ entryTypeId, params }: CreateEntryTypeVersionVariables) =>
            EntryTypeApi.create(entryTypeId, params),
    });
}

function setDraft(entryTypeId: string, version: number, update: (draft: EntryTypeDraft) => EntryTypeDraft): void {
    queryClient.setQueryData<EntryTypeDraft>(editEntryTypeKeys.detail(entryTypeId, version), draft =>
        draft ? update(draft) : draft,
    );
}

function setServiceIds(entryTypeId: string, version: number, update: (serviceIds: string[]) => string[]): void {
    queryClient.setQueryData<string[]>(editEntryTypeKeys.services(entryTypeId, version), serviceIds =>
        serviceIds ? update(serviceIds) : serviceIds,
    );
}
